import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";

import {
  Notification,
  NotificationType,
} from "@transfr-inc/dashboard-components";
import { Button } from "@transfr-inc/dashboard-components/forms";

import { JobOutlook } from "@components/job-outlook/job-outlook";
import { CareerPlanGoalsModal, CareerSkillsModal } from "@components/modals";
import { CheckedCell } from "@components/table-cells";
import { useApiRequest, useDownloadPathwayPDF } from "@lib/hooks";
import { getTotalCareerGoals } from "@models";

import container from "@src/container";
import { useCareerImageUrl } from "@src/hooks/queries";
import { ANNUAL_HOURS_WORKED } from "@src/services";

import "./trainee-career-pathway.section.scss";

const percentileIndicators = {
  min: 10,
  max: 90,
  average: 50,
};

const getHourlyEarningsValue = (data, percentile) => {
  const earning = data?.find((d) => d.percentile === percentile)?.earnings ?? 0;
  // Once the CESE financial literacy changes are deployed the earnings will be
  // annual, so the below can be simplified.

  // If earnings are greater than $1000/hour, then most likely "earning" is
  // annual instead of hourly
  if (earning > 1000) {
    return Math.trunc(earning / ANNUAL_HOURS_WORKED);
  }
  return Math.trunc(earning);
};

const getEarningsRange = (data) => {
  return {
    min: getHourlyEarningsValue(data, percentileIndicators.min),
    max: getHourlyEarningsValue(data, percentileIndicators.max),
    average: getHourlyEarningsValue(data, percentileIndicators.average),
  };
};

export const TraineeCareerPathwaySection = ({
  classroom,
  downloadPathwayPDFEnabled,
  toolbar,
  user,
}) => {
  const [openGoalsModal, setOpenGoalsModal] = useState(false);
  const [openSkillsModal, setOpenSkillsModal] = useState(false);
  const [notification, setNotification] = useState();

  const { trekService, toggles } = container;
  const { response: journeyStatus = {} } = useApiRequest(() =>
    trekService.getUserJourneyStatus(user.userId)
  );

  const {
    response: {
      career,
      id: careerPlanId,
      goals: careerPlanGoals,
      submittedAt,
    } = {},
    loading: loadingCareerPlan,
  } = useApiRequest(() => trekService.getCareerPlan(user?.userId));

  const {
    response: { goals: budgetPlanGoals } = {},
    loading: loadingBudgetPlan,
  } = useApiRequest(() => trekService.getBudgetPlan(user?.userId));

  const { isGeneratingPDF, doDownloadPathwayPDF } = useDownloadPathwayPDF({
    classroom,
    trainee: user,
    setNotification,
  });

  const { data: imageUri } = useCareerImageUrl(
    { transfrId: career?.transfrId },
    { enabled: !!career?.transfrId }
  );

  const careerLocalData = career?.regional ?? career?.national ?? [];

  const regionalEarnings = getEarningsRange(careerLocalData?.earnings);
  const totalGoals = getTotalCareerGoals({
    ...careerPlanGoals,
    budget: budgetPlanGoals,
  });

  const pathSteps = useMemo(() => {
    let steps = [
      {
        completed: journeyStatus?.intInv,
        name: "Get Ready",
      },
      {
        completed: journeyStatus?.matches,
        name: "Explore",
      },
      {
        completed: journeyStatus?.budget,
        name: "Create Budget",
      },
      {
        completed:
          journeyStatus?.highSch ||
          journeyStatus?.postHighSch ||
          journeyStatus?.share ||
          journeyStatus?.goals,
        name: "Selected Pathway",
      },
      {
        completed: journeyStatus?.goals,
        name: "Setup Goals",
      },
    ];

    return steps;
  }, [journeyStatus]);

  useEffect(() => {
    if (isGeneratingPDF) {
      setNotification({
        type: NotificationType.info,
        message: "Generating PDF...",
        icon: ["fa-solid", "spinner"],
        iconConfig: { spin: true },
      });
    } else {
      setNotification();
    }
  }, [isGeneratingPDF]);

  return (
    <>
      <div className="trainee-pathway-section-container">
        <div className="trainee-pathway-section-header">
          <h2>Career Pathway</h2>
          <div className="trainee-pathway-section-header-toolbar">
            {toolbar}
            {downloadPathwayPDFEnabled && (
              <Button
                size="small"
                primary
                icon={["fa-light", "fa-file-pdf"]}
                onClick={() => doDownloadPathwayPDF(careerPlanId)}
                disabled={!submittedAt || isGeneratingPDF}
              >
                View Full Pathway
              </Button>
            )}
          </div>
        </div>
        <div className="trainee-pathway-section-lower-container">
          {submittedAt ? (
            <div className="trainee-pathway-section-info-container">
              <div className="pathway-image">
                <img
                  className="path-img"
                  src={imageUri}
                  alt={`${career?.title} career icon`}
                ></img>
              </div>
              <div className="pathway-info">
                <div className="pathway-info-title">{career?.title}</div>
                <div className="pathway-info-subtitle">
                  {career?.shortDescription}
                </div>
                <div className="career-info-main-container">
                  <div className="career-info-container">
                    ${regionalEarnings.average}/hr
                  </div>
                  <div className="career-info-container">
                    <div className="status">
                      <JobOutlook
                        value={careerLocalData?.outlook}
                        national={!career?.regional}
                      />
                    </div>
                  </div>
                  <div className="career-info-container">
                    Goals{" "}
                    <button
                      className="link-button"
                      onClick={() => setOpenGoalsModal(true)}
                    >
                      {totalGoals}
                    </button>
                  </div>
                  <div className="career-info-container">
                    Skills{" "}
                    <button
                      className="link-button"
                      onClick={() => setOpenSkillsModal(true)}
                    >
                      {career?.skills?.length || 0}
                    </button>
                  </div>
                </div>
                <div className="disclaimer">
                  *Data gathered from recent job descriptions.
                </div>
              </div>
            </div>
          ) : (
            <div className="trek-zero-state-container">
              <div className="trek-zero-state">
                {loadingCareerPlan ? (
                  <>
                    <FontAwesomeIcon icon={["fa-solid", "spinner"]} spin />
                    Loading career pathway...
                  </>
                ) : (
                  <>
                    <FontAwesomeIcon icon={"fa-solid fa-hiking"} />
                    Trainee hasn&apos;t selected a career pathway in Trek
                  </>
                )}
              </div>
            </div>
          )}
          <div className="trek-progress-main-container">
            <div className="current-step">Current Step </div>
            {pathSteps.map((step) => (
              <div key={step.name} className="trek-progress-steps">
                <CheckedCell value={step.completed} />
                <div>{step.name}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
      {createPortal(
        <>
          <CareerPlanGoalsModal
            open={openGoalsModal}
            onClose={() => setOpenGoalsModal()}
            user={user}
            goals={{ ...careerPlanGoals, budget: budgetPlanGoals }}
          />
          <CareerSkillsModal
            open={openSkillsModal}
            onClose={() => setOpenSkillsModal()}
            user={user}
            skills={career?.skills}
          />
        </>,
        document.body
      )}
      {createPortal(
        notification && (
          <Notification
            type={notification.type}
            icon={notification.icon}
            iconConfig={notification.iconConfig}
            onClose={() => setNotification()}
            closable
          >
            {notification.message}
          </Notification>
        ),
        document.querySelector(".app-notification-stack")
      )}
    </>
  );
};
